import { API_BASE_URL } from '../config/api';

/** Backend error codes surfaced by the billing API. */
export const BILLING_ERROR_CODES = {
  TRIAL_ACTIVE: 'TRIAL_ACTIVE',
  INSUFFICIENT_PIXELS: 'INSUFFICIENT_PIXELS',
  NO_ACTIVE_ENTITLEMENT: 'NO_ACTIVE_ENTITLEMENT',
  NO_ACTIVE_SUBSCRIPTION: 'NO_ACTIVE_SUBSCRIPTION',
} as const;

export class BillingError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, fields?: { status?: number; code?: string }) {
    super(message);
    this.name = 'BillingError';
    if (fields?.status !== undefined) this.status = fields.status;
    if (fields?.code !== undefined) this.code = fields.code;
  }
}

export interface SubscriptionPrice {
  id: string;
  price: number | null;
  currency: string | null;
  pixelsPerPeriod: number | null;
  interval: string | null;
}

export interface PixelPurchasePrice {
  pricePerPack: number | null;
  pixelsPerPack: number | null;
}

export interface BillingPrices {
  subscriptions: SubscriptionPrice[];
  pixelPurchase: PixelPurchasePrice | null;
}

export interface BillingSubscription {
  status: string | null;
  plan: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface CheckoutSession {
  url: string;
  sessionId: string | null;
}

export type CheckoutPayload =
  | { mode: 'pixel_purchase'; quantity: number }
  | { mode: 'subscription'; planId: string };

const toFiniteNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const toNonEmptyStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

const toOptionalStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const authHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
});

/** Extract a backend error code from a parsed JSON body, if present. */
const readErrorCode = (parsed: any): string | undefined => {
  const code = parsed?.code ?? parsed?.error?.code ?? parsed?.data?.code;
  return typeof code === 'string' && code !== '' ? code : undefined;
};

const readErrorMessage = (parsed: any, fallback: string): string => {
  if (typeof parsed?.error === 'string' && parsed.error !== '')
    return parsed.error;
  if (typeof parsed?.message === 'string' && parsed.message !== '')
    return parsed.message;
  return fallback;
};

const parseSubscriptionPrice = (raw: unknown): SubscriptionPrice | null => {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = toNonEmptyStringOrNull(record.id);
  if (!id) return null;
  return {
    id,
    price:
      toFiniteNumberOrNull(record.price) ??
      toFiniteNumberOrNull(record.amount) ??
      null,
    currency:
      toOptionalStringOrNull(record.currency) ??
      toOptionalStringOrNull(record.currencyCode),
    pixelsPerPeriod:
      toFiniteNumberOrNull(record.pixelsPerPeriod) ??
      toFiniteNumberOrNull(record.pixels) ??
      null,
    interval: toOptionalStringOrNull(record.interval),
  };
};

const parsePixelPurchasePrice = (raw: unknown): PixelPurchasePrice | null => {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  return {
    pricePerPack:
      toFiniteNumberOrNull(record.pricePerPack) ??
      toFiniteNumberOrNull(record.price) ??
      null,
    pixelsPerPack:
      toFiniteNumberOrNull(record.pixelsPerPack) ??
      toFiniteNumberOrNull(record.pixels) ??
      null,
  };
};

/**
 * GET /api/billing/prices. Parses defensively — the backend may omit fields;
 * callers treat nulls as "unknown" and render fallbacks instead of
 * hardcoded prices.
 */
export const getPrices = async (token: string): Promise<BillingPrices> => {
  const response = await fetch(`${API_BASE_URL}/billing/prices`, {
    method: 'GET',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw new BillingError('Failed to load billing prices', {
      status: response.status,
    });
  }

  const parsed: any = await response.json();
  const rawSubscriptions = Array.isArray(parsed?.subscriptions)
    ? parsed.subscriptions
    : Array.isArray(parsed?.plans)
      ? parsed.plans
      : [];
  const subscriptions = rawSubscriptions
    .map(parseSubscriptionPrice)
    .filter(
      (entry: SubscriptionPrice | null): entry is SubscriptionPrice =>
        entry !== null
    );
  const pixelPurchase =
    parsePixelPurchasePrice(parsed?.pixelPurchase) ??
    parsePixelPurchasePrice(parsed?.pixelPack) ??
    null;

  return { subscriptions, pixelPurchase };
};

/**
 * POST /api/billing/checkout/session. Pixel packs take ONLY `{mode:
 * "pixel_purchase", quantity}` (1–25, never a pixel amount); subscriptions
 * take `{mode: "subscription", planId}`. Returns the Stripe-hosted Checkout
 * URL — the caller redirects with `window.location.href = url`.
 */
export const createCheckoutSession = async (
  token: string,
  payload: CheckoutPayload
): Promise<CheckoutSession> => {
  const response = await fetch(`${API_BASE_URL}/billing/checkout/session`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let code: string | undefined;
    let message = 'Failed to create checkout session';
    try {
      const parsed: any = await response.json();
      code = readErrorCode(parsed);
      message = readErrorMessage(parsed, message);
    } catch {
      /* non-JSON error body — keep the fallback message */
    }
    throw new BillingError(message, { status: response.status, code });
  }

  const parsed: any = await response.json();
  const url = toNonEmptyStringOrNull(parsed?.url);
  if (!url) {
    throw new BillingError('Checkout session did not include a URL');
  }
  return {
    url,
    sessionId:
      toNonEmptyStringOrNull(parsed?.sessionId) ??
      toNonEmptyStringOrNull(parsed?.session_id) ??
      toNonEmptyStringOrNull(parsed?.id),
  };
};

/** POST /api/billing/portal — returns the Stripe customer-portal URL. */
export const createPortalSession = async (
  token: string
): Promise<{ url: string }> => {
  const response = await fetch(`${API_BASE_URL}/billing/portal`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw new BillingError('Failed to open the billing portal', {
      status: response.status,
    });
  }

  const parsed: any = await response.json();
  const url = toNonEmptyStringOrNull(parsed?.url);
  if (!url) {
    throw new BillingError('Billing portal did not include a URL');
  }
  return { url };
};

export type CancelReason =
  | 'too_expensive'
  | 'not_using'
  | 'missing_features'
  | 'quality'
  | 'switched'
  | 'other'
  | 'unspecified';

export interface CancelSubscriptionPayload {
  reason?: CancelReason;
  details?: string;
}

export const CANCEL_REASONS: CancelReason[] = [
  'too_expensive',
  'not_using',
  'missing_features',
  'quality',
  'switched',
  'other',
];

export const CANCEL_REASON_LABELS: Record<string, string> = {
  too_expensive: 'Too expensive',
  not_using: "I'm not using it enough",
  missing_features: 'Missing features I need',
  quality: "Quality wasn't good enough",
  switched: 'Switched to something else',
  other: 'Something else',
};

export const MAX_CANCEL_DETAILS_LENGTH = 500;

/** Build the POST body for cancel: trim details, omit when empty. */
export const buildCancelPayload = (
  reason: CancelReason,
  details: string
): CancelSubscriptionPayload => {
  const trimmed = details.trim().slice(0, MAX_CANCEL_DETAILS_LENGTH);
  return trimmed ? { reason, details: trimmed } : { reason };
};

/** True when the subscription view means "scheduled to cancel". */
export const isCancelScheduled = (
  subscription: BillingSubscription | null
): boolean => subscription?.cancelAtPeriodEnd === true;

/** Banner copy for a scheduled cancellation; guards null period end. */
export const cancelBannerDate = (periodEnd: string | null): string => {
  if (!periodEnd) return 'end of billing period';
  const date = new Date(periodEnd);
  if (Number.isNaN(date.getTime())) return periodEnd;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/** GET /api/billing/subscription — current subscription state, if any. */
export const getSubscription = async (
  token: string
): Promise<BillingSubscription> => {
  const response = await fetch(`${API_BASE_URL}/billing/subscription`, {
    method: 'GET',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    let code: string | undefined;
    try {
      const parsed: any = await response.json();
      code = readErrorCode(parsed);
    } catch {
      /* ignore — code stays undefined */
    }
    throw new BillingError('Failed to load subscription', {
      status: response.status,
      code,
    });
  }

  const parsed: any = await response.json();
  return {
    status: toOptionalStringOrNull(parsed?.status),
    plan:
      toOptionalStringOrNull(parsed?.plan) ??
      toOptionalStringOrNull(parsed?.planId),
    currentPeriodStart:
      toOptionalStringOrNull(parsed?.currentPeriodStart) ??
      toOptionalStringOrNull(parsed?.current_period_start),
    currentPeriodEnd:
      toOptionalStringOrNull(parsed?.currentPeriodEnd) ??
      toOptionalStringOrNull(parsed?.current_period_end),
    cancelAtPeriodEnd: parsed?.cancelAtPeriodEnd === true,
  };
};

/**
 * POST /api/billing/subscription/cancel — schedule cancellation at period
 * end. Idempotent: a second call is a no-op returning the same view.
 */
export const cancelSubscription = async (
  token: string,
  payload: CancelSubscriptionPayload
): Promise<BillingSubscription> => {
  const response = await fetch(`${API_BASE_URL}/billing/subscription/cancel`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let code: string | undefined;
    let message = 'Failed to cancel subscription';
    try {
      const parsed: any = await response.json();
      code = readErrorCode(parsed);
      message = readErrorMessage(parsed, message);
    } catch {
      /* non-JSON error body — keep the fallback message */
    }
    throw new BillingError(message, { status: response.status, code });
  }

  const parsed: any = await response.json();
  return {
    status: toOptionalStringOrNull(parsed?.status),
    plan:
      toOptionalStringOrNull(parsed?.plan) ??
      toOptionalStringOrNull(parsed?.planId),
    currentPeriodStart:
      toOptionalStringOrNull(parsed?.currentPeriodStart) ??
      toOptionalStringOrNull(parsed?.current_period_start),
    currentPeriodEnd:
      toOptionalStringOrNull(parsed?.currentPeriodEnd) ??
      toOptionalStringOrNull(parsed?.current_period_end),
    cancelAtPeriodEnd: parsed?.cancelAtPeriodEnd === true,
  };
};
